import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Grid,
  MenuItem,
  Select,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Card,
  CardContent,
  Skeleton,
  Fade,
  Slide,
  IconButton,
  useTheme,
  useMediaQuery,
  Chip,
  Stack,
  Container,
} from '@mui/material';
import {
  TrendingUp,
  Warehouse,
  Inventory,
  Assessment,
  TableChart,
  ExpandMore,
  ExpandLess,
  Refresh,
  Download,
  FilterList,
  Schedule,
} from '@mui/icons-material';
import WarehouseContext from '../context/WarehouseContext';
import ThemeModeContext from '../context/ThemeModeContext';
import { get } from '../services/apiMethods';
import URLS from '../services/urlConstants';
import ShowComponent from '../components/ShowComponent';
import DashBoardPageHeaderPage from '../components/DashBoard/DashBoardHeaderPage';

export default function Dashboard() {
  const { selectedWarehouse, selectedOwnerCode, selectedOwnerGroup } = useContext(WarehouseContext);
  const { isDarkMode } = useContext(ThemeModeContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  const [mode, setMode] = useState('Storage');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [refreshing, setRefreshing] = useState(false);

  // Initialize all collapsible sections as collapsed by default
  useEffect(() => {
    if (data && data.warehouseInfo && mode === 'Storage') {
      const initialState = {};
      data.warehouseInfo.forEach(warehouse => {
        initialState[`${warehouse.code}-category`] = false;
        initialState[`${warehouse.code}-stock`] = false;
        initialState[`${warehouse.code}-order`] = false;
        initialState[`${warehouse.code}-orderref`] = false;
        initialState[`${warehouse.code}-laptops`] = false;
      });
      setExpandedSections(initialState);
    }
  }, [data, mode]);

  const fetchSummary = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const params = {
        warehouses: selectedWarehouse,
        ownerCodes: selectedOwnerCode,
        ownerGroups: selectedOwnerGroup,
      };
      const url =
        mode === 'Storage' ? URLS.dashboard.storageSummary : URLS.dashboard.inProcessSummary;
      const { data } = await get(url, { params });
      console.log('Dashboard API Response for mode:', mode, data);
      console.log('Dashboard Result Data:', data.result);

      // Debug each table structure
      if (data.result) {
        if (mode === 'Storage') {
          console.log('STORAGE MODE - Data keys:', Object.keys(data.result));
          console.log('table1_warehouseTotals:', data.result.table1_warehouseTotals);
          console.log('table2_customerWise:', data.result.table2_customerWise);
          console.log('perWarehouseTables:', data.result.perWarehouseTables);
          console.log('allWarehouseOrderType:', data.result.allWarehouseOrderType);
          console.log('orderReferenceTables:', data.result.orderReferenceTables);
          console.log('laptopModelTables:', data.result.laptopModelTables);
          console.log('warehouseInfo:', data.result.warehouseInfo);
        } else {
          console.log('IN-PROCESS MODE - Data keys:', Object.keys(data.result));
          console.log('activityCodeTotals:', data.result.activityCodeTotals);
          console.log('warehouseTotals:', data.result.warehouseTotals);
          console.log('ownerCodeTotals:', data.result.ownerCodeTotals);
          console.log('activityBySubFamily:', data.result.activityBySubFamily);
          console.log('activityByDate:', data.result.activityByDate);
          console.log('activityByMonth:', data.result.activityByMonth);
          console.log('agingAnalysis:', data.result.agingAnalysis);

          // Check which tables have data
          console.log('=== TABLE DATA CHECK ===');
          console.log(
            'activityCodeTotals has data:',
            !!(
              data.result.activityCodeTotals &&
              data.result.activityCodeTotals.rows &&
              data.result.activityCodeTotals.rows.length
            )
          );
          console.log(
            'activityBySubFamily has data:',
            !!(
              data.result.activityBySubFamily &&
              data.result.activityBySubFamily.rows &&
              data.result.activityBySubFamily.rows.length
            )
          );
          console.log(
            'activityByDate has data:',
            !!(
              data.result.activityByDate &&
              data.result.activityByDate.rows &&
              data.result.activityByDate.rows.length
            )
          );
          console.log(
            'activityByMonth has data:',
            !!(
              data.result.activityByMonth &&
              data.result.activityByMonth.rows &&
              data.result.activityByMonth.rows.length
            )
          );
          console.log(
            'agingAnalysis has data:',
            !!(
              data.result.agingAnalysis &&
              data.result.agingAnalysis.rows &&
              data.result.agingAnalysis.rows.length
            )
          );
        }
      }

      setData(data.result);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchSummary(true);
  };

  const toggleSection = sectionKey => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const toggleAllSections = (expand = true) => {
    if (data && data.warehouseInfo && mode === 'Storage') {
      const newState = {};
      data.warehouseInfo.forEach(warehouse => {
        newState[`${warehouse.code}-category`] = expand;
        newState[`${warehouse.code}-stock`] = expand;
        newState[`${warehouse.code}-order`] = expand;
        newState[`${warehouse.code}-orderref`] = expand;
        newState[`${warehouse.code}-laptops`] = expand;
      });
      setExpandedSections(newState);
    }
  };

  useEffect(() => {
    // Call fetchSummary when context is ready and filters are available
    // For non-super users, the backend will filter based on their assigned permissions
    // even if frontend arrays are empty
    if (selectedWarehouse?.length && selectedOwnerCode?.length && selectedOwnerGroup?.length) {
      fetchSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mode,
    selectedWarehouse?.join(','),
    selectedOwnerCode?.join(','),
    selectedOwnerGroup?.join(','),
  ]);

  const renderTable = (
    tableData,
    title,
    icon = TableChart,
    collapsible = false,
    sectionKey = ''
  ) => {
    console.log(`renderTable called for "${title}":`, {
      hasTableData: !!tableData,
      hasColumns: !!(tableData && tableData.columns),
      hasRows: !!(tableData && tableData.rows),
      rowsLength: tableData && tableData.rows ? tableData.rows.length : 0,
      tableData,
    });

    if (!tableData || !tableData.columns || !tableData.rows || tableData.rows.length === 0) {
      return (
        <Card
          elevation={0}
          sx={{
            mb: 3,
            border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
            borderRadius: 3,
            background: isDarkMode
              ? 'linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.05) 100%)'
              : 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  borderRadius: 2,
                  bgcolor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                  mr: 2,
                }}
              >
                {React.createElement(icon, {
                  sx: {
                    fontSize: 18,
                    color: isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
                  },
                })}
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: isMobile ? '1rem' : '1.1rem',
                  color: isDarkMode ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.8)',
                }}
              >
                {title}
              </Typography>
            </Box>
            <Typography
              color="text.secondary"
              sx={{
                fontSize: '0.875rem',
                fontStyle: 'italic',
              }}
            >
              No data available
            </Typography>
          </CardContent>
        </Card>
      );
    }

    const isExpanded = collapsible ? expandedSections[sectionKey] === true : true;

    return (
      <Fade in timeout={400}>
        <Card
          elevation={0}
          sx={{
            mb: { xs: 2, sm: 3 },
            border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
            borderRadius: { xs: 2, sm: 2.5 },
            background: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.8)',
            backdropFilter: 'blur(8px)',
            transition: 'all 0.2s ease',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: isDarkMode ? '0 4px 20px rgba(0,0,0,0.2)' : '0 4px 20px rgba(0,0,0,0.08)',
            },
          }}
        >
          <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: isExpanded ? { xs: 1.5, sm: 2 } : 0,
                cursor: collapsible ? 'pointer' : 'default',
              }}
              onClick={collapsible ? () => toggleSection(sectionKey) : undefined}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: { xs: 24, sm: 28 },
                    height: { xs: 24, sm: 28 },
                    borderRadius: 1.5,
                    bgcolor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                  }}
                >
                  {React.createElement(icon, {
                    sx: {
                      fontSize: { xs: 14, sm: 16 },
                      color: theme.palette.primary.main,
                    },
                  })}
                </Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: '0.85rem', sm: '0.9rem' },
                    color: isDarkMode ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.8)',
                    lineHeight: 1.3,
                  }}
                >
                  {String(title).toUpperCase()}
                </Typography>
              </Box>
              {collapsible && (
                <IconButton size="small" sx={{ p: 0.5 }}>
                  {isExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              )}
            </Box>

            {isExpanded && (
              <Slide in={isExpanded} direction="up" timeout={300}>
                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                  <TableContainer
                    sx={{
                      borderRadius: { xs: 1.5, sm: 2 },
                      border: `1px solid ${
                        isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'
                      }`,
                      maxHeight: { xs: '200px', sm: '250px' },
                      overflowY: 'auto',
                      '&::-webkit-scrollbar': {
                        width: '6px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                        borderRadius: '3px',
                      },
                    }}
                  >
                    <Table size="small" stickyHeader sx={{ minWidth: 'max-content' }}>
                      <TableHead>
                        <TableRow>
                          {tableData.columns.map((col, idx) => (
                            <TableCell
                              key={idx}
                              sx={{
                                fontWeight: 600,
                                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                bgcolor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
                                color: isDarkMode ? 'rgba(255,255,255,0.95)' : 'rgba(0,0,0,0.87)',
                                borderBottom: `2px solid ${theme.palette.primary.main}`,
                                whiteSpace: 'nowrap',
                                position: 'sticky',
                                top: 0,
                                zIndex: 1,
                                py: { xs: 0.5, sm: 0.75 },
                                px: { xs: 0.5, sm: 1 },
                                minWidth:
                                  idx === 0
                                    ? { xs: '120px', sm: '140px' }
                                    : { xs: '60px', sm: '80px' },
                                backdropFilter: 'blur(8px)',
                                boxShadow: isDarkMode
                                  ? '0 1px 3px rgba(0,0,0,0.3)'
                                  : '0 1px 3px rgba(0,0,0,0.1)',
                                ...(col === 'Grand Total' && {
                                  bgcolor: isDarkMode
                                    ? 'rgba(25, 104, 56, 0.25)'
                                    : 'rgba(25, 104, 56, 0.15)',
                                  color: theme.palette.primary.main,
                                  fontWeight: 700,
                                  minWidth: { xs: '80px', sm: '100px' },
                                }),
                              }}
                            >
                              {String(col).toUpperCase()}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {tableData.rows.map((row, idx) => {
                          const isGrandTotal = row[tableData.columns[0]] === 'Grand Total';
                          return (
                            <TableRow
                              key={idx}
                              sx={{
                                transition: 'background-color 0.2s ease',
                                '&:hover': {
                                  bgcolor: isDarkMode
                                    ? 'rgba(255,255,255,0.05)'
                                    : 'rgba(0,0,0,0.02)',
                                },
                                ...(isGrandTotal && {
                                  bgcolor: isDarkMode
                                    ? 'rgba(25, 104, 56, 0.1)'
                                    : 'rgba(25, 104, 56, 0.05)',
                                  '&:hover': {
                                    bgcolor: isDarkMode
                                      ? 'rgba(25, 104, 56, 0.15)'
                                      : 'rgba(25, 104, 56, 0.08)',
                                  },
                                }),
                              }}
                            >
                              {tableData.columns.map((col, colIdx) => (
                                <TableCell
                                  key={colIdx}
                                  sx={{
                                    fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                    fontWeight: isGrandTotal ? 600 : 400,
                                    color: isGrandTotal ? theme.palette.primary.main : 'inherit',
                                    borderBottom: `1px solid ${
                                      isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'
                                    }`,
                                    py: { xs: 0.25, sm: 0.5 },
                                    px: { xs: 0.5, sm: 1 },
                                    whiteSpace: colIdx === 0 ? 'normal' : 'nowrap',
                                    maxWidth: colIdx === 0 ? { xs: '150px', sm: '180px' } : 'none',
                                    overflow: colIdx === 0 ? 'hidden' : 'visible',
                                    textOverflow: colIdx === 0 ? 'ellipsis' : 'clip',
                                  }}
                                >
                                  {typeof row[col] === 'number' && row[col] > 0 ? (
                                    <Chip
                                      label={row[col].toLocaleString()}
                                      size="small"
                                      variant={isGrandTotal ? 'filled' : 'outlined'}
                                      color={isGrandTotal ? 'primary' : 'default'}
                                      sx={{
                                        fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                        height: { xs: 16, sm: 20 },
                                        minWidth: { xs: '28px', sm: '32px' },
                                        '& .MuiChip-label': {
                                          px: { xs: 0.5, sm: 1 },
                                        },
                                        ...(isGrandTotal && {
                                          fontWeight: 600,
                                        }),
                                      }}
                                    />
                                  ) : (
                                    <span title={String(row[col] || 0).toUpperCase()}>
                                      {' '}
                                      {/* Add tooltip for long text */}
                                      {String(row[col] || 0).toUpperCase()}
                                    </span>
                                  )}
                                </TableCell>
                              ))}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Add scroll indicator for mobile */}
                  {isMobile && tableData.columns.length > 3 && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: 'block',
                        textAlign: 'center',
                        mt: 1,
                        fontSize: '0.7rem',
                        fontStyle: 'italic',
                      }}
                    >
                      ← Scroll horizontally to view all columns →
                    </Typography>
                  )}
                </Box>
              </Slide>
            )}
          </CardContent>
        </Card>
      </Fade>
    );
  };

  // Destructure data based on mode (moved outside render functions for accessibility)
  const {
    // Storage mode tables
    table1_warehouseTotals,
    table2_customerWise,
    perWarehouseTables,
    allWarehouseOrderType,
    orderReferenceTables,
    laptopModelTables,
    warehouseInfo,

    // In-Process mode tables
    activityCodeTotals,
    warehouseTotals,
    ownerCodeTotals,
    activityBySubFamily,
    activityByDate,
    activityByMonth,
    agingAnalysis,
  } = data || {};

  const renderStorage = () => {
    if (loading) {
      return (
        <Box sx={{ py: 2 }}>
          <Stack spacing={3}>
            {[...Array(6)].map((_, idx) => (
              <Card key={idx} elevation={0} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                <CardContent sx={{ p: 3 }}>
                  <Stack spacing={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Skeleton
                        variant="rectangular"
                        width={32}
                        height={32}
                        sx={{ borderRadius: 1 }}
                      />
                      <Skeleton variant="text" width={300} height={28} />
                    </Box>
                    <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Box>
      );
    }

    if (!data) {
      console.log('No data available for mode:', mode);
      return null;
    }

    console.log('Rendering dashboard with data for mode:', mode, 'Data keys:', Object.keys(data));

    return (
      <Box sx={{ py: { xs: 0.5, sm: 1.5 } }}>
        <Stack spacing={{ xs: 1.5, sm: 2 }}>
          {/* Storage Summary Section */}
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: { xs: 1.5, sm: 2 },
                p: { xs: 1, sm: 1.5 },
                borderRadius: { xs: 2, sm: 2.5 },
                background: `linear-gradient(135deg, ${theme.palette.primary.main}12 0%, ${theme.palette.primary.main}06 100%)`,
                border: `1px solid ${theme.palette.primary.main}15`,
              }}
            >
              <TrendingUp
                sx={{ fontSize: { xs: 18, sm: 20 }, color: theme.palette.primary.main, mr: 1 }}
              />
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    lineHeight: 1.2,
                  }}
                >
                  STORAGE OVERVIEW
                </Typography>
                {!isMobile && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.7rem', mt: 0.25 }}
                  >
                    INVENTORY ANALYSIS ACROSS LOCATIONS
                  </Typography>
                )}
              </Box>
            </Box>

            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              <Grid item xs={12}>
                {renderTable(table1_warehouseTotals, 'TOTAL STOCK BY WAREHOUSE', Warehouse)}
              </Grid>
              <Grid item xs={12}>
                {renderTable(table2_customerWise, 'CUSTOMER-WISE DISTRIBUTION', Assessment)}
              </Grid>
            </Grid>
          </Box>

          {/* Compact Warehouse Analysis */}
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: { xs: 1.5, sm: 2 },
                p: { xs: 1, sm: 1.5 },
                borderRadius: { xs: 2, sm: 2.5 },
                background: `linear-gradient(135deg, ${theme.palette.secondary.main}12 0%, ${theme.palette.secondary.main}06 100%)`,
                border: `1px solid ${theme.palette.secondary.main}15`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Warehouse
                  sx={{ fontSize: { xs: 18, sm: 20 }, color: theme.palette.secondary.main }}
                />
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.secondary.main,
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      lineHeight: 1.2,
                    }}
                  >
                    WAREHOUSE ANALYSIS
                  </Typography>
                  {!isMobile && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.7rem', mt: 0.25 }}
                    >
                      BREAKDOWN BY CATEGORY, STOCK & ORDER TYPE
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Compact Expand/Collapse All Controls */}
              <Stack direction="row" spacing={0.5}>
                <IconButton
                  size="small"
                  onClick={() => toggleAllSections(true)}
                  sx={{
                    bgcolor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                    '&:hover': {
                      bgcolor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.06)',
                    },
                    p: 0.5,
                  }}
                  title="Expand All"
                >
                  <ExpandMore
                    sx={{ fontSize: { xs: 16, sm: 18 }, color: theme.palette.secondary.main }}
                  />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => toggleAllSections(false)}
                  sx={{
                    bgcolor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                    '&:hover': {
                      bgcolor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.06)',
                    },
                    p: 0.5,
                  }}
                  title="Collapse All"
                >
                  <ExpandLess
                    sx={{ fontSize: { xs: 16, sm: 18 }, color: theme.palette.secondary.main }}
                  />
                </IconButton>
              </Stack>
            </Box>

            <Grid container spacing={{ xs: 2, sm: 3 }}>
              {warehouseInfo?.map((warehouse, idx) => (
                <Grid item xs={12} key={idx}>
                  <Card
                    elevation={0}
                    sx={{
                      border: `1px solid ${
                        isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'
                      }`,
                      borderRadius: { xs: 2, sm: 2.5 },
                      background: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.8)',
                      backdropFilter: 'blur(8px)',
                    }}
                  >
                    <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mb: { xs: 1.5, sm: 2 },
                          gap: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: { xs: 28, sm: 32 },
                            height: { xs: 28, sm: 32 },
                            borderRadius: 1.5,
                            bgcolor: theme.palette.primary.main + '15',
                          }}
                        >
                          <Warehouse
                            sx={{ fontSize: { xs: 14, sm: 16 }, color: theme.palette.primary.main }}
                          />
                        </Box>
                        <Box>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 600,
                              color: theme.palette.primary.main,
                              fontSize: { xs: '0.9rem', sm: '1rem' },
                              lineHeight: 1.2,
                            }}
                          >
                            {warehouse.code?.toUpperCase()}
                          </Typography>
                          {!isMobile && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: '0.7rem', mt: 0.25 }}
                            >
                              {warehouse.name?.toUpperCase()}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      <Grid container spacing={{ xs: 1, sm: 1.5 }}>
                        <Grid item xs={12}>
                          {renderTable(
                            perWarehouseTables?.[warehouse.code]?.itemCategory,
                            'ITEM CATEGORY',
                            Inventory,
                            true,
                            `${warehouse.code}-category`
                          )}
                        </Grid>
                        <Grid item xs={12}>
                          {renderTable(
                            perWarehouseTables?.[warehouse.code]?.stockType,
                            'STOCK TYPE',
                            Assessment,
                            true,
                            `${warehouse.code}-stock`
                          )}
                        </Grid>
                        <Grid item xs={12}>
                          {renderTable(
                            perWarehouseTables?.[warehouse.code]?.orderType,
                            'ORDER TYPE',
                            TableChart,
                            true,
                            `${warehouse.code}-order`
                          )}
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* In-Process: Activity Code Totals (single row) */}
          {mode === 'In-Process' && activityCodeTotals && (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: { xs: 1.5, sm: 2 },
                  p: { xs: 1, sm: 1.5 },
                  borderRadius: { xs: 2, sm: 2.5 },
                  background: `linear-gradient(135deg, ${theme.palette.info.main}12 0%, ${theme.palette.info.main}06 100%)`,
                  border: `1px solid ${theme.palette.info.main}15`,
                }}
              >
                <Assessment
                  sx={{ fontSize: { xs: 18, sm: 20 }, color: theme.palette.info.main, mr: 1 }}
                />
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.info.main,
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      lineHeight: 1.2,
                    }}
                  >
                    ACTIVITY CODE TOTALS
                  </Typography>
                  {!isMobile && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.7rem', mt: 0.25 }}
                    >
                      OVERALL IN-PROCESS ACTIVITY CODE DISTRIBUTION
                    </Typography>
                  )}
                </Box>
              </Box>

              {renderTable(activityCodeTotals, 'OVERALL ACTIVITY CODE TOTALS', Assessment)}
            </Box>
          )}

          {/* Compact Overall Summary */}
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: { xs: 1.5, sm: 2 },
                p: { xs: 1, sm: 1.5 },
                borderRadius: { xs: 2, sm: 2.5 },
                background: `linear-gradient(135deg, #ff6b6b12 0%, #ff6b6b06 100%)`,
                border: `1px solid #ff6b6b15`,
              }}
            >
              <Assessment sx={{ fontSize: { xs: 18, sm: 20 }, color: '#ff6b6b', mr: 1 }} />
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: '#ff6b6b',
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    lineHeight: 1.2,
                  }}
                >
                  CONSOLIDATED SUMMARY
                </Typography>
                {!isMobile && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.7rem', mt: 0.25 }}
                  >
                    CROSS-WAREHOUSE ORDER TYPE ANALYSIS
                  </Typography>
                )}
              </Box>
            </Box>

            {renderTable(allWarehouseOrderType, 'ALL WAREHOUSES - ORDER TYPE ANALYSIS', Assessment)}
          </Box>

          {/* Order Reference Analysis */}
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: { xs: 1.5, sm: 2 },
                p: { xs: 1, sm: 1.5 },
                borderRadius: { xs: 2, sm: 2.5 },
                background: `linear-gradient(135deg, #4ecdc412 0%, #4ecdc406 100%)`,
                border: `1px solid #4ecdc415`,
              }}
            >
              <TableChart sx={{ fontSize: { xs: 18, sm: 20 }, color: '#4ecdc4', mr: 1 }} />
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: '#4ecdc4',
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    lineHeight: 1.2,
                  }}
                >
                  ORDER REFERENCE ANALYSIS
                </Typography>
                {!isMobile && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.7rem', mt: 0.25 }}
                  >
                    INVENTORY DISTRIBUTION BY ORDER REFERENCE NUMBERS
                  </Typography>
                )}
              </Box>
            </Box>

            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              {warehouseInfo?.map((warehouse, idx) => (
                <Grid item xs={12} key={idx}>
                  {renderTable(
                    orderReferenceTables?.[warehouse.code],
                    `${warehouse.code?.toUpperCase()} - ORDER REFERENCES`,
                    TableChart,
                    true,
                    `${warehouse.code}-orderref`
                  )}
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Laptop Model Analysis */}
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: { xs: 1.5, sm: 2 },
                p: { xs: 1, sm: 1.5 },
                borderRadius: { xs: 2, sm: 2.5 },
                background: `linear-gradient(135deg, #45b7d112 0%, #45b7d106 100%)`,
                border: `1px solid #45b7d115`,
              }}
            >
              <Inventory sx={{ fontSize: { xs: 18, sm: 20 }, color: '#45b7d1', mr: 1 }} />
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: '#45b7d1',
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    lineHeight: 1.2,
                  }}
                >
                  LAPTOP MODEL ANALYSIS
                </Typography>
                {!isMobile && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.7rem', mt: 0.25 }}
                  >
                    DETAILED BREAKDOWN OF LAPTOP INVENTORY BY MODEL
                  </Typography>
                )}
              </Box>
            </Box>

            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              {warehouseInfo?.map((warehouse, idx) => (
                <Grid item xs={12} key={idx}>
                  {renderTable(
                    laptopModelTables?.[warehouse.code],
                    `${warehouse.code?.toUpperCase()} - LAPTOP MODELS`,
                    Inventory,
                    true,
                    `${warehouse.code}-laptops`
                  )}
                </Grid>
              ))}
            </Grid>
          </Box>
        </Stack>
      </Box>
    );
  };

  const renderInProcess = () => {
    if (loading) {
      return (
        <Box sx={{ py: 2 }}>
          <Stack spacing={3}>
            {[...Array(5)].map((_, idx) => (
              <Card key={idx} elevation={0} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                <CardContent sx={{ p: 3 }}>
                  <Stack spacing={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Skeleton
                        variant="rectangular"
                        width={32}
                        height={32}
                        sx={{ borderRadius: 1 }}
                      />
                      <Skeleton variant="text" width={300} height={28} />
                    </Box>
                    <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Box>
      );
    }

    if (!data) {
      console.log('No in-process data available');
      return null;
    }

    console.log('Rendering in-process dashboard with data:', Object.keys(data));

    return (
      <Box sx={{ py: { xs: 0.5, sm: 1.5 } }}>
        <Stack spacing={{ xs: 1.5, sm: 2 }}>
          {/* Activity Code Totals (Sheet1 from Excel) */}
          {activityCodeTotals && (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: { xs: 1.5, sm: 2 },
                  p: { xs: 1, sm: 1.5 },
                  borderRadius: { xs: 2, sm: 2.5 },
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}12 0%, ${theme.palette.primary.main}06 100%)`,
                  border: `1px solid ${theme.palette.primary.main}15`,
                }}
              >
                <Assessment
                  sx={{ fontSize: { xs: 18, sm: 20 }, color: theme.palette.primary.main, mr: 1 }}
                />
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.primary.main,
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      lineHeight: 1.2,
                    }}
                  >
                    ACTIVITY CODE SUMMARY
                  </Typography>
                  {!isMobile && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.7rem', mt: 0.25 }}
                    >
                      TOTAL IN-PROCESS ITEMS BY ACTIVITY CODE
                    </Typography>
                  )}
                </Box>
              </Box>
              {renderTable(activityCodeTotals, 'ACTIVITY CODE TOTALS', Assessment)}
            </Box>
          )}

          {/* Warehouse Totals */}
          {warehouseTotals && (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: { xs: 1.5, sm: 2 },
                  p: { xs: 1, sm: 1.5 },
                  borderRadius: { xs: 2, sm: 2.5 },
                  background: `linear-gradient(135deg, ${theme.palette.secondary.main}12 0%, ${theme.palette.secondary.main}06 100%)`,
                  border: `1px solid ${theme.palette.secondary.main}15`,
                }}
              >
                <Warehouse
                  sx={{ fontSize: { xs: 18, sm: 20 }, color: theme.palette.secondary.main, mr: 1 }}
                />
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.secondary.main,
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      lineHeight: 1.2,
                    }}
                  >
                    WAREHOUSE SUMMARY
                  </Typography>
                  {!isMobile && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.7rem', mt: 0.25 }}
                    >
                      TOTAL IN-PROCESS ITEMS BY WAREHOUSE
                    </Typography>
                  )}
                </Box>
              </Box>
              {renderTable(warehouseTotals, 'WAREHOUSE TOTALS', Warehouse)}
            </Box>
          )}

          {/* Owner Code Totals */}
          {ownerCodeTotals && (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: { xs: 1.5, sm: 2 },
                  p: { xs: 1, sm: 1.5 },
                  borderRadius: { xs: 2, sm: 2.5 },
                  background: `linear-gradient(135deg, #ff6b6b12 0%, #ff6b6b06 100%)`,
                  border: `1px solid #ff6b6b15`,
                }}
              >
                <TrendingUp sx={{ fontSize: { xs: 18, sm: 20 }, color: '#ff6b6b', mr: 1 }} />
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: '#ff6b6b',
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      lineHeight: 1.2,
                    }}
                  >
                    CUSTOMER SUMMARY
                  </Typography>
                  {!isMobile && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.7rem', mt: 0.25 }}
                    >
                      TOTAL IN-PROCESS ITEMS BY CUSTOMER
                    </Typography>
                  )}
                </Box>
              </Box>
              {renderTable(ownerCodeTotals, 'CUSTOMER TOTALS', TrendingUp)}
            </Box>
          )}

          {/* TABLE 2: Activity Code by Sub Family Pivot */}
          {activityBySubFamily &&
          activityBySubFamily.rows &&
          activityBySubFamily.rows.length > 0 ? (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: { xs: 1.5, sm: 2 },
                  p: { xs: 1, sm: 1.5 },
                  borderRadius: { xs: 2, sm: 2.5 },
                  background: `linear-gradient(135deg, #4ecdc412 0%, #4ecdc406 100%)`,
                  border: `1px solid #4ecdc415`,
                }}
              >
                <TableChart sx={{ fontSize: { xs: 18, sm: 20 }, color: '#4ecdc4', mr: 1 }} />
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: '#4ecdc4',
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      lineHeight: 1.2,
                    }}
                  >
                    ACTIVITY CODE BY SUB FAMILY
                  </Typography>
                  {!isMobile && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.7rem', mt: 0.25 }}
                    >
                      CROSS-TABULATION OF ACTIVITY CODES AND DEVICE TYPES
                    </Typography>
                  )}
                </Box>
              </Box>
              {renderTable(activityBySubFamily, 'ACTIVITY CODE BY SUB FAMILY', TableChart)}
            </Box>
          ) : (
            <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
              <Typography variant="body2">
                TABLE 2: Activity Code by Sub Family - No data available
              </Typography>
            </Box>
          )}

          {/* TABLE 3: Activity Code by Date */}
          {activityByDate && activityByDate.rows && activityByDate.rows.length > 0 ? (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: { xs: 1.5, sm: 2 },
                  p: { xs: 1, sm: 1.5 },
                  borderRadius: { xs: 2, sm: 2.5 },
                  background: `linear-gradient(135deg, #ff980012 0%, #ff980006 100%)`,
                  border: `1px solid #ff980015`,
                }}
              >
                <Schedule sx={{ fontSize: { xs: 18, sm: 20 }, color: '#ff9800', mr: 1 }} />
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: '#ff9800',
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      lineHeight: 1.2,
                    }}
                  >
                    ACTIVITY CODE BY DATE
                  </Typography>
                  {!isMobile && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.7rem', mt: 0.25 }}
                    >
                      DAILY BREAKDOWN OF IN-PROCESS ITEMS
                    </Typography>
                  )}
                </Box>
              </Box>
              {renderTable(activityByDate, 'ACTIVITY CODE BY DATE', Schedule)}
            </Box>
          ) : (
            <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
              <Typography variant="body2">
                TABLE 3: Activity Code by Date - No data available
              </Typography>
            </Box>
          )}

          {/* TABLE 4: Activity Code by Month */}
          {activityByMonth && activityByMonth.rows && activityByMonth.rows.length > 0 ? (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: { xs: 1.5, sm: 2 },
                  p: { xs: 1, sm: 1.5 },
                  borderRadius: { xs: 2, sm: 2.5 },
                  background: `linear-gradient(135deg, #3f51b512 0%, #3f51b506 100%)`,
                  border: `1px solid #3f51b515`,
                }}
              >
                <Assessment sx={{ fontSize: { xs: 18, sm: 20 }, color: '#3f51b5', mr: 1 }} />
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: '#3f51b5',
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      lineHeight: 1.2,
                    }}
                  >
                    ACTIVITY CODE BY MONTH
                  </Typography>
                  {!isMobile && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.7rem', mt: 0.25 }}
                    >
                      MONTHLY BREAKDOWN OF IN-PROCESS ITEMS
                    </Typography>
                  )}
                </Box>
              </Box>
              {renderTable(activityByMonth, 'ACTIVITY CODE BY MONTH', Assessment)}
            </Box>
          ) : (
            <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
              <Typography variant="body2">
                TABLE 4: Activity Code by Month - No data available
              </Typography>
            </Box>
          )}

          {/* TABLE 5: Aging Analysis */}
          {agingAnalysis && agingAnalysis.rows && agingAnalysis.rows.length > 0 ? (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: { xs: 1.5, sm: 2 },
                  p: { xs: 1, sm: 1.5 },
                  borderRadius: { xs: 2, sm: 2.5 },
                  background: `linear-gradient(135deg, #9c27b012 0%, #9c27b006 100%)`,
                  border: `1px solid #9c27b015`,
                }}
              >
                <Schedule sx={{ fontSize: { xs: 18, sm: 20 }, color: '#9c27b0', mr: 1 }} />
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: '#9c27b0',
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      lineHeight: 1.2,
                    }}
                  >
                    AGING ANALYSIS
                  </Typography>
                  {!isMobile && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.7rem', mt: 0.25 }}
                    >
                      IN-PROCESS ITEMS BY TIME IN SYSTEM
                    </Typography>
                  )}
                </Box>
              </Box>
              {renderTable(agingAnalysis, 'AGING ANALYSIS', Schedule)}
            </Box>
          ) : (
            <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
              <Typography variant="body2">TABLE 5: Aging Analysis - No data available</Typography>
            </Box>
          )}
        </Stack>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: isDarkMode ? 'rgba(0,0,0,0.01)' : 'rgba(248,250,252,0.5)',
        backgroundImage: isDarkMode
          ? 'radial-gradient(circle at 20% 50%, rgba(25,104,56,0.03) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(68,98,133,0.03) 0%, transparent 50%)'
          : 'radial-gradient(circle at 20% 50%, rgba(25,104,56,0.02) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(68,98,133,0.02) 0%, transparent 50%)',
      }}
    >
      <DashBoardPageHeaderPage title="Dashboard" />

      {/* Ultra Compact Header Section */}
      <Box sx={{ py: { xs: 0.25, sm: 0.5 }, px: { xs: 1.5, sm: 2 } }}>
        <Card
          elevation={0}
          sx={{
            mb: { xs: 0.75, sm: 1 },
            border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
            borderRadius: { xs: 1.5, sm: 2 },
            background: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.7)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <CardContent sx={{ p: { xs: 0.75, sm: 1 } }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1, sm: 1.25 },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1 } }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: { xs: 24, sm: 28 },
                    height: { xs: 24, sm: 28 },
                    borderRadius: 1,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  }}
                >
                  <Assessment sx={{ fontSize: { xs: 12, sm: 14 }, color: 'white' }} />
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.primary.main,
                      fontSize: { xs: '0.75rem', sm: '0.8rem' },
                      lineHeight: 1.2,
                      textTransform: 'uppercase',
                    }}
                  >
                    {mode === 'Storage' ? 'STORAGE ANALYTICS' : 'IN-PROCESS ANALYTICS'}
                  </Typography>
                  {!isMobile && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: { xs: '0.6rem', sm: '0.65rem' },
                        mt: 0.125,
                        display: 'block',
                        textTransform: 'uppercase',
                      }}
                    >
                      {mode === 'Storage'
                        ? 'REAL-TIME INVENTORY INSIGHTS'
                        : 'REAL-TIME IN-PROCESS TRACKING'}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Stack direction="row" spacing={{ xs: 0.75, sm: 1 }} alignItems="center">
                <IconButton
                  onClick={handleRefresh}
                  disabled={refreshing}
                  size="small"
                  sx={{
                    width: { xs: 28, sm: 32 },
                    height: { xs: 28, sm: 32 },
                    bgcolor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                    '&:hover': {
                      bgcolor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.06)',
                    },
                    transition: 'all 0.15s ease',
                  }}
                >
                  <Refresh
                    sx={{
                      fontSize: { xs: 14, sm: 16 },
                      color: theme.palette.primary.main,
                      animation: refreshing ? 'spin 1s linear infinite' : 'none',
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' },
                      },
                    }}
                  />
                </IconButton>

                <Select
                  size="small"
                  value={mode}
                  onChange={e => setMode(e.target.value)}
                  sx={{
                    minWidth: { xs: 120, sm: 140 },
                    fontSize: { xs: '0.75rem', sm: '0.8rem' },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      bgcolor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.7)',
                      '&:hover': {
                        bgcolor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.8)',
                      },
                    },
                    '& .MuiSelect-select': {
                      py: { xs: 0.5, sm: 0.75 },
                      textTransform: 'uppercase',
                    },
                  }}
                >
                  <MenuItem value="Storage">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Warehouse sx={{ fontSize: { xs: 12, sm: 14 } }} />
                      <Typography
                        sx={{
                          fontSize: { xs: '0.75rem', sm: '0.8rem' },
                          textTransform: 'uppercase',
                        }}
                      >
                        Storage Report
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="In-Process">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Assessment sx={{ fontSize: { xs: 12, sm: 14 } }} />
                      <Typography
                        sx={{
                          fontSize: { xs: '0.75rem', sm: '0.8rem' },
                          textTransform: 'uppercase',
                        }}
                      >
                        In-Process Report
                      </Typography>
                    </Box>
                  </MenuItem>
                </Select>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {mode === 'Storage' ? renderStorage() : renderInProcess()}
    </Box>
  );
}
